'use client'
import * as React from 'react'
import { OTPInput, OTPInputContext } from 'input-otp'

import { cn } from '@/lib/utils'
import { Minus } from 'lucide-react'
import { env } from '@/env/client'
import type { JSX } from 'react'

type InputOTPComponentProps = React.ComponentProps<typeof OTPInput> & {
  /**
   * Transaction name for RUM monitoring.
   */
  transactionName: string
}
const InputOTP = React.forwardRef<
  React.ComponentRef<typeof OTPInput>,
  InputOTPComponentProps
>(({ className, containerClassName, transactionName, ...props }, ref) => (
  <OTPInput
    ref={ref}
    containerClassName={cn(
      'flex items-center gap-2 has-[:disabled]:opacity-50',
      containerClassName
    )}
    className={cn('disabled:cursor-not-allowed', className)}
    data-transaction-name={transactionName}
    {...props}
  />
))
InputOTP.displayName = 'InputOTP'

const InputOTPGroup = React.forwardRef<
  React.ComponentRef<'div'>,
  React.ComponentPropsWithoutRef<'div'>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex items-center', className)} {...props} />
))
InputOTPGroup.displayName = 'InputOTPGroup'

const InputOTPSlot = React.forwardRef<
  React.ComponentRef<'div'>,
  React.ComponentPropsWithoutRef<'div'> & { index: number }
>(({ index, className, ...props }, ref) => {
  const inputOTPContext = React.useContext(OTPInputContext)
  const { char, hasFakeCaret, isActive } = inputOTPContext.slots[index]!

  return (
    <div
      ref={ref}
      className={cn(
        'relative flex h-12 w-[3.3rem] items-center justify-center border-y border-input text-sm transition-all first:rounded-l-md first:border-l last:rounded-r-md md:w-16 lg:w-16',

        index < inputOTPContext.slots.length - 1 ? '' : 'border-r',
        isActive && 'z-10 ring-ring',
        className
      )}
      {...props}
    >
      {char ? '•' : ''}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-px animate-caret-blink bg-foreground duration-1000" />
        </div>
      )}
    </div>
  )
})
InputOTPSlot.displayName = 'InputOTPSlot'

const InputOTPSeparator = React.forwardRef<
  React.ComponentRef<'div'>,
  React.ComponentPropsWithoutRef<'div'>
>(({ ...props }, ref) => (
  <div ref={ref} {...props}>
    <Minus className="text-muted-foreground" />
  </div>
))
InputOTPSeparator.displayName = 'InputOTPSeparator'

interface OTPInputProps {
  maxLength: number
  separator: number | number[]
  onChange?: (value: string) => void
  value?: string
}

const OTPField: React.FC<OTPInputProps> = ({
  maxLength,
  separator: seperator,
  onChange,
  ...props
}) => {
  const renderGroups = () => {
    const elements: JSX.Element[] = []
    let currentIndex = 0
    if (typeof seperator === 'number') {
      while (currentIndex < maxLength) {
        const nextIndex = currentIndex + seperator
        elements.push(
          <InputOTPGroup key={currentIndex}>
            {Array.from({ length: seperator }, (_, i) => currentIndex + i).map(
              (i) => (
                <InputOTPSlot key={i} index={i} />
              )
            )}
          </InputOTPGroup>
        )
        if (nextIndex < maxLength) {
          elements.push(<InputOTPSeparator key={`separator-${currentIndex}`} />)
        }
        currentIndex = nextIndex
      }
    }
    return elements
  }

  /**
   * Trims the input value to the maximum allowed length and triggers the onChange callback.
   * Ensures pasted or programmatically entered values do not exceed maxLength.
   */

  const handleChange = (
    value: string,
    maxLength: number,
    onChange?: (v: string) => void,
    prevValue: string = ''
  ) => {
    const prev = prevValue
    if (!/^\d*$/.test(value)) return

    if (value.length < prev?.length) {
      onChange?.(value)
      return
    }
    if (value.length === prev?.length + 1) {
      if (value.length > env.NEXT_PUBLIC_OTP_LENGTH) return

      onChange?.(value)
    }
    if (value.length > maxLength) return
  }

  return (
    <InputOTP
      maxLength={maxLength}
      onChange={(v) => handleChange(v, maxLength, onChange, props?.value)}
      transactionName="component.input-otp"
      {...props}
    >
      {renderGroups()}
    </InputOTP>
  )
}

export default OTPField
